import InfoIcon from "@mui/icons-material/Info";
import { Alert, Box, Card, Collapse, Radio, Stack, Switch, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";

import { useAppSelector } from "../../../../../redux/hooks/redux";
import { AppCard } from "../../../../apps-integration/components/AppCard";
import { useAppGrid } from "../../../../apps-integration/hooks/useAppGrid";
import { colors, alphaVar } from "../../../../ui/colors";
import { useCompanyPaymentsStatusQuery } from "../../hooks/usePayments";
import { IAvailablePaymens } from "../../interfaces/invoice.interfaces";
import { BankTransferCard } from "../BankTransfer/components/BankTransferCard";
import { usePaymentMethodState, useSetPaymentMethod } from "../states/usePaymentMethod";
import { PaymentMethodState, SelectedPayment } from "../types";
import { PaymentMethodItem } from "./PaymentMethodItem";

interface PaymentMethodSelectorProps {
  availablePayments: IAvailablePaymens;
  canMarkAsPaid?: boolean;
  bypassCompanyStatus?: boolean;
}

const BANK_TRANSFER_ID = "bank_transfer" as SelectedPayment;
const BANK_TRANSFER_STORAGE_KEY = "userPaymentMethod.bank_transfer.enabled";

const uniq = (arr: SelectedPayment[]) => Array.from(new Set(arr));
const isAllSelected = (allIds: SelectedPayment[], selected: SelectedPayment[]) => {
  const sel = new Set(selected);
  return allIds.length > 0 && allIds.every((id) => sel.has(id));
};

export const PaymentMethodSelector = ({ availablePayments, canMarkAsPaid = true, bypassCompanyStatus = false }: PaymentMethodSelectorProps) => {
  const { getAppsForCategory } = useAppGrid();
  const company = useAppSelector((state) => state.userState.company);
  const { data } = useCompanyPaymentsStatusQuery(company?.id ?? "");

  const allPaymentApps = getAppsForCategory("payments");

  const {
    markAsPaid,
    manualEntryNotes,
    manualEntryTransactionId,
    selectedPayments: selectedIds,
    method,
  }: PaymentMethodState = usePaymentMethodState();
  const { setField } = useSetPaymentMethod();

  const availablePaymentMethods = useMemo(() => {
    return allPaymentApps?.length > 0
      ? allPaymentApps
          .filter((app) => {
            if (app.id === "paypal") return !!availablePayments?.paypal;
            if (app.id === "stripe") return !!availablePayments?.stripe && (bypassCompanyStatus || !!data?.stripe);
            return false;
          })
          .sort((a, b) => (a.id === "paypal" ? -1 : b.id === "paypal" ? 1 : 0))
      : [];
  }, [allPaymentApps, availablePayments, data, bypassCompanyStatus]);

  const bankTransferEnabled = useMemo(() => {
    if (bypassCompanyStatus) return false;
    const saved = localStorage.getItem(BANK_TRANSFER_STORAGE_KEY);
    return saved == null ? true : saved === "true";
  }, [bypassCompanyStatus]);

  const ALL_IDS = useMemo<SelectedPayment[]>(() => {
    const base = availablePaymentMethods.map((m) => m.id as SelectedPayment);
    return bankTransferEnabled ? [...base, BANK_TRANSFER_ID] : base;
  }, [availablePaymentMethods, bankTransferEnabled]);

  const toggle = useCallback(
    (id: SelectedPayment) => {
      const current = selectedIds ?? [];
      const next = current.includes(id) ? current.filter((s) => s !== id) : [...current, id];
      setField("selectedPayments", next);
    },
    [selectedIds, setField],
  );

  const toggleSelectAll = useCallback(() => {
    const current = uniq(selectedIds ?? []);
    const shouldSelectAll = !isAllSelected(ALL_IDS, current);
    setField("selectedPayments", shouldSelectAll ? [...ALL_IDS] : []);
  }, [ALL_IDS, selectedIds, setField]);

  useEffect(() => {
    if (!bankTransferEnabled && selectedIds?.includes(BANK_TRANSFER_ID)) {
      setField(
        "selectedPayments",
        selectedIds.filter((id) => id !== BANK_TRANSFER_ID),
      );
    }
  }, [bankTransferEnabled, selectedIds, setField]);

  useEffect(() => {
    if (method !== "online") {
      setField("method", "online");
    }
  }, [method, setField]);

  const noneSelected = !selectedIds || selectedIds.length === 0;
  const allSelected = useMemo(() => isAllSelected(ALL_IDS, selectedIds ?? []), [ALL_IDS, selectedIds]);

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: "none",
        p: 3,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: colors.gray[600] }}>
        Payment Method
      </Typography>

      {/* Mark as Paid Toggle */}
      {canMarkAsPaid && (
        <Box sx={{ mb: 3, pb: 3, borderBottom: `1px solid ${alphaVar(colors.gray[200], 0.5)}` }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: markAsPaid ? 2 : 0,
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ color: colors.gray[700], mb: 0.5 }}>
                Mark invoice as paid (Manual Entry)
              </Typography>
              <Typography variant="caption" sx={{ color: colors.gray[500] }}>
                Mark this invoice as paid immediately without payment gateway
              </Typography>
            </Box>
            <Switch
              checked={markAsPaid}
              onChange={(e) => {
                e.stopPropagation();
                setField("markAsPaid", e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              color="primary"
            />
          </Box>

          {markAsPaid && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="Payment Notes (Optional)"
                value={manualEntryNotes}
                onChange={(e) => setField("manualEntryNotes", e.target.value)}
                placeholder="e.g., Cash payment, Check #1234, etc."
                multiline
                rows={2}
                size="small"
                fullWidth
              />
              <TextField
                label="Transaction ID (Optional)"
                value={manualEntryTransactionId}
                onChange={(e) => setField("manualEntryTransactionId", e.target.value)}
                placeholder="e.g., Check number, receipt ID, etc."
                size="small"
                fullWidth
              />
            </Stack>
          )}
        </Box>
      )}
      {/* Payment Methods */}
      <Collapse in={!markAsPaid} timeout={300}>
        <Box>
          {noneSelected && (
            <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 2, borderRadius: 1 }}>
              Please select at least one payment method or mark as paid to continue
            </Alert>
          )}

          <Stack spacing={1.5}>
            {availablePaymentMethods.map((payment) => (
              <PaymentMethodItem
                key={payment.id}
                selected={selectedIds?.includes(payment.id as SelectedPayment) ?? false}
                onToggle={() => toggle(payment.id as SelectedPayment)}
              >
                <AppCard app={payment} cardVariant="payment" />
              </PaymentMethodItem>
            ))}

            {bankTransferEnabled && (
              <PaymentMethodItem selected={selectedIds?.includes(BANK_TRANSFER_ID) ?? false} onToggle={() => toggle(BANK_TRANSFER_ID)}>
                <BankTransferCard bankVariant="payments" />
              </PaymentMethodItem>
            )}
          </Stack>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 2,
              p: 1.5,
              bgcolor: alphaVar(colors.gray[50] || "var(--fx-surface-sunken, #F8FAFC)", 0.5),
              borderRadius: 1,
              cursor: "pointer",
            }}
            onClick={toggleSelectAll}
          >
            <Radio
              checked={allSelected}
              size="small"
              sx={{ "&.Mui-checked": { color: colors.orange[500] } }}
              onClick={(e) => {
                e.stopPropagation();
                toggleSelectAll();
              }}
            />
            <Typography variant="body2" sx={{ ml: 1, color: colors.gray[300] }}>
              Select all payment methods
            </Typography>
          </Box>
        </Box>
      </Collapse>

      {/* Info Alert cuando markAsPaid */}
      <Collapse in={markAsPaid} timeout={300}>
        <Alert
          severity="info"
          icon={<InfoIcon />}
          sx={{
            mt: 2,
            bgcolor: alphaVar(colors.info[50], 0.5),
            border: `1px solid ${alphaVar(colors.info[200], 0.3)}`,
            borderRadius: 2,
            boxShadow: "none",
            "& .MuiAlert-icon": { color: colors.info[600] },
            "& .MuiAlert-message": { color: colors.info[700] },
          }}
        >
          <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
            This invoice will be marked as paid immediately. Payment methods are not required when using manual entry.
          </Typography>
        </Alert>
      </Collapse>
    </Card>
  );
};
